import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "../../../store";
import { Box, Grid2 } from "@mui/material";
import Connection from "../../../types/connection";
import { useForm, SubmitHandler } from "react-hook-form";
import FormTextField from "../../forms/FormTextField";
import FormAutocomplete from "../../forms/FormAutocomplete";
import {
  fetchAuthTypes,
  fetchSystemTypes,
  testConnection,
} from "../../../slices/connection";
import Button from "../../base/Button";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import SystemType from "../../../types/systemType";
import Option from "../../../types/option";

interface FormProps {
  type: "add" | "edit";
  onSubmit: SubmitHandler<Connection>;
}

export type ConnectionFormSubmitHandler = () => void;

const AddEditConnection = forwardRef<ConnectionFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { type, onSubmit } = props;
    const dispatch = useDispatch();

    const {
      connection,
      systemTypes,
      authenticationTypes,
      testStatus,
      formStatus,
      connectionValid,
    } = useSelector((store) => store.connection);

    const { settings } = useSelector((store) => store.settings);

    const [enabledSystems, setEnabledSystems] = useState(systemTypes);

    const { handleSubmit, control, watch, reset, setValue } =
      useForm<Connection>({
        mode: "onChange",
        defaultValues: connection,
      });

    const formValues = watch();

    useImperativeHandle(ref, () => handleSubmit(onSubmit));

    const { loadWithSkeleton } = useSkeletonLoader(formStatus === "loading");

    const getAuthTypes = (systemType: SystemType) => {
      setValue("authenticationType", null);
      setValue("username", "");
      setValue("password", "");

      if (!!systemType) {
        dispatch(fetchAuthTypes(systemType.id)).then((result) => {
          if (result && result.payload) {
            let types = result.payload as Option[];
            if (!!types && types?.length === 1) {
              setValue("authenticationType", types[0]);
            }
          }
        });
      }
    };

    const handleTest = (connection: Connection) => {
      dispatch(testConnection(connection));
    };

    useEffect(() => {
      if (!!settings && !!settings.enabledSystems) {
        let allowedSystems = systemTypes?.filter((x) =>
          settings.enabledSystems?.some((c) => c.id === x.id)
        );

        setEnabledSystems(allowedSystems);
      }
    }, [settings, systemTypes]);

    useEffect(() => {
      dispatch(fetchSystemTypes());
      if (type === "edit" && !!connection) {
        dispatch(fetchAuthTypes(connection?.systemType?.id!));
      }
    }, [dispatch, connection, type]);

    useEffect(() => {
      if (!!connection) {
        reset(connection);
      }

      return () => {
        reset();
      };
    }, [reset, connection]);

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    label="Name"
                    control={control}
                    fieldName="name"
                    required
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="systemType"
                    options={enabledSystems || []}
                    required
                    label="System"
                    onChange={getAuthTypes}
                    defaultValue={formValues.systemType || null}
                    staticFilter
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="authenticationType"
                    options={authenticationTypes || []}
                    required
                    label="Authentication Type"
                    noOptionsText={
                      !!formValues.systemType
                        ? "No options available"
                        : "Select system type to load authentication types"
                    }
                    defaultValue={formValues.authenticationType || null}
                    staticFilter
                  />
                )}
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 6 }}
                display={!formValues.systemType ? "block" : "none"}
              />
              {formValues &&
                formValues.systemType &&
                formValues.systemType?.connectionType?.id === 3 && (
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormTextField
                        control={control}
                        fieldName={"tenant"}
                        label={"Tenant Id"}
                        required
                      />
                    )}
                  </Grid2>
                )}
              {formValues &&
                formValues.systemType &&
                (formValues.systemType?.connectionType?.id === 1 ||
                  formValues.systemType?.connectionType?.id === 2) && (
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormTextField
                        control={control}
                        fieldName={"server"}
                        label={"Server"}
                        required
                      />
                    )}
                  </Grid2>
                )}
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {formValues &&
                  formValues.authenticationType &&
                  (formValues.authenticationType.id === 1 ||
                    formValues.authenticationType.id === 3) &&
                  loadWithSkeleton(
                    <FormTextField
                      control={control}
                      fieldName="username"
                      label="Username"
                      required
                    />
                  )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {formValues &&
                  formValues.authenticationType &&
                  (formValues.authenticationType.id === 1 ||
                    formValues.authenticationType.id === 3) &&
                  loadWithSkeleton(
                    <FormTextField
                      control={control}
                      fieldName="password"
                      label="Password"
                      type="password"
                      required
                    />
                  )}
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 6, md: 4 }}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  label="Test"
                  loading={testStatus === "loading"}
                  color={
                    connectionValid === true
                      ? "success"
                      : connectionValid === false
                      ? "error"
                      : "info"
                  }
                  onClick={() => handleTest(formValues)}
                  tooltip="Click to test connection"
                  type="button"
                  disabled={!formValues.authenticationType}
                />
              </Grid2>
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  }
);

export default AddEditConnection;
